// libs/ui/src/primitives/Button.tsx
import { useCallback, type ComponentProps } from 'react';
import { ActivityIndicator } from 'react-native';
import { styled, Button as TamaguiButton, XStack } from 'tamagui';
import { useThemeColors } from '../hooks/useThemeColors';
import { useHaptics } from '../hooks/useHaptics';

// Subtle press feedback — opacity dip plus a tiny scale-down. Animation
// preset 'quicker' is ~300ms ease-out, fast enough to feel responsive
// but slow enough to register as motion. Pressed backgrounds read from
// the per-accent press tokens introduced in ADR-0072.
const PRESSED = { opacity: 0.8, scale: 0.97 } as const;
const PRESSED_OUTLINE = { opacity: 0.7, scale: 0.97 } as const;

// Focus ring — applied via focusVisibleStyle (web :focus-visible).
// outlineColor resolves to $focusRingColor (string token); width and
// offset are numeric (the Tamagui config filters numeric values out of
// themeable tokens, so they're inlined here).
const FOCUS_VISIBLE = {
  outlineColor: '$focusRingColor',
  outlineWidth: 2,
  outlineStyle: 'solid' as never,
  outlineOffset: 2,
} as const;

const StyledButton = styled(TamaguiButton, {
  name: 'Button',
  borderRadius: '$2',
  transition: 'quicker',
  hoverStyle: { scale: 1.01 },
  focusVisibleStyle: FOCUS_VISIBLE,

  variants: {
    variant: {
      primary: {
        backgroundColor: '$accentCerulean',
        color: 'white',
        hoverStyle: { backgroundColor: '$accentCeruleanHover', scale: 1.01 },
        pressStyle: { ...PRESSED, backgroundColor: '$accentCeruleanPressed' },
      },
      success: {
        backgroundColor: '$accentGreen',
        color: 'white',
        hoverStyle: { backgroundColor: '$accentGreenHover', scale: 1.01 },
        pressStyle: { ...PRESSED, backgroundColor: '$accentGreenPressed' },
      },
      warning: {
        backgroundColor: '$accentOrange',
        color: 'white',
        hoverStyle: { backgroundColor: '$accentOrangeHover', scale: 1.01 },
        pressStyle: { ...PRESSED, backgroundColor: '$accentOrangePressed' },
      },
      danger: {
        backgroundColor: '$accentCrimson',
        color: 'white',
        hoverStyle: { backgroundColor: '$accentCrimsonHover', scale: 1.01 },
        pressStyle: { ...PRESSED, backgroundColor: '$accentCrimsonPressed' },
      },
      outline: {
        backgroundColor: 'transparent',
        color: '$textOnSurface',
        borderWidth: 1,
        borderColor: '$borderLight',
        hoverStyle: { backgroundColor: '$surfaceHover', scale: 1.01 },
        pressStyle: { ...PRESSED_OUTLINE, backgroundColor: '$surfacePressed' },
      },
      ghost: {
        backgroundColor: 'transparent',
        color: '$textOnSurface',
        borderWidth: 0,
        borderColor: 'transparent',
        hoverStyle: { backgroundColor: '$surfaceHover', scale: 1.01 },
        pressStyle: PRESSED_OUTLINE,
      },
      icon: {
        borderWidth: 0,
        borderColor: 'transparent',
        borderRadius: '$10',
        width: 44,
        height: 44,
        padding: 0,
        pressStyle: PRESSED_OUTLINE,
      },
    },
  } as const,

  defaultVariants: {
    variant: 'primary',
  },
});

type StyledButtonProps = ComponentProps<typeof StyledButton>;

export interface ButtonStateProps {
  /** Disables press + applies $disabledOpacity. Sets accessibilityState. */
  disabled?: boolean;
  /** Replaces label with an inline spinner. Press disabled while loading. */
  loading?: boolean;
}

export type ButtonProps = StyledButtonProps & ButtonStateProps;

/**
 * Primary interactive primitive. Adds `disabled` and `loading` states to
 * Tamagui's Button per ADR-0074:
 * - `disabled` dims to `$disabledOpacity`, blocks press, announces
 *   `accessibilityState={{disabled:true}}` + `aria-disabled` on web.
 * - `loading` swaps the label for an inline `ActivityIndicator`,
 *   announces `accessibilityState={{busy:true}}` + `aria-busy`, and
 *   blocks press.
 *
 * `variant="icon"` callers must pass `accessibilityLabel`. This isn't
 * type-enforced (Tamagui's variant types make discriminated unions
 * brittle in practice); enforcement lands via a focused ESLint rule in
 * Phase 6's primitive-state audit.
 */
export function Button(props: ButtonProps) {
  const { disabled, loading, onPress, children, variant, ...rest } = props;
  const theme = useThemeColors();
  const haptics = useHaptics();
  const isBlocked = disabled || loading;

  // Per ADR-0079 fire-strength table: primary/success/warning/danger
  // → light tap. icon/ghost stay silent (minor affordances).
  const shouldFireHaptic =
    variant === 'primary' ||
    variant === 'success' ||
    variant === 'warning' ||
    variant === 'danger' ||
    variant === undefined;

  const handlePress = useCallback(
    (event: Parameters<NonNullable<typeof onPress>>[0]) => {
      if (shouldFireHaptic) haptics.light();
      onPress?.(event);
    },
    [onPress, haptics, shouldFireHaptic],
  );

  const blockedProps = isBlocked
    ? {
        onPress: undefined,
        accessibilityState: { disabled: !!disabled, busy: !!loading },
        opacity: theme.disabledOpacity,
        ...(disabled ? { 'aria-disabled': true } : null),
        ...(loading ? { 'aria-busy': true } : null),
      }
    : { onPress: handlePress, accessibilityState: { disabled: false, busy: false } };

  if (loading) {
    return (
      <StyledButton {...rest} variant={variant} {...blockedProps}>
        <XStack alignItems="center" gap="$2">
          <ActivityIndicator size="small" />
          {children}
        </XStack>
      </StyledButton>
    );
  }

  return (
    <StyledButton {...rest} variant={variant} {...blockedProps}>
      {children}
    </StyledButton>
  );
}
